import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClipboardList, Plus, RefreshCw, ChevronLeft, ChevronRight, SearchX } from 'lucide-react';
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  type ExpandedState,
  type SortingState,
  type PaginationState,
} from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { PermissionGuard } from '@/components/PermissionGuard';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { useAuthStore } from '@features/authentication/store/authStore';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DataTableRow } from '@/components/ui/data-table-row';
import { EmptyState } from '@/components/ui/empty-state';
import { useTableStateParams } from '@/hooks/useTableStateParams';
import { TableSkeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/ui/page-header';
import { usePicklistStore } from '../store';
import { createPicklistColumns } from './picklist-table-columns';
import { PicklistFilterBar } from './PicklistFilterBar';
import { ProgressLegend } from './PicklistProgressBits';
import { MrsHoverCardHost } from './mrs-hover-card';
import type { PicklistDto, PicklistParentRow, PicklistItemRow } from '../types';
import { PostingStatusEnum } from '../types';

/**
 * PW-12 — the picklist landing.
 *
 * ## Store subscriptions are narrow on purpose (86d3d1z40)
 *
 * This component renders the table. Destructuring the whole store (`const { ... } = usePicklistStore()`)
 * subscribes it to every slice, including the 3-second settle-poll — so the table re-rendered on a timer
 * whether or not anything it draws had changed. Every read below is a selector returning a VALUE.
 *
 * ## The search box is a table filter now, and the deep link survives
 *
 * It used to be a suggestion dropdown that `window.open`'d a detail page and never touched the table. A
 * filter is the right call — the filtered rows carry progress, status, MRS chips and expansion the
 * three-line suggestion never did — but the one-keystroke jump was a real capability. It survives as
 * **Enter with exactly one match**, in-app, with a visible hint saying so.
 */

/**
 * One sub-row per ITEM (PT-7 D2): the server's `itemGroups`, on the Open and the Closed tab alike. `items[]`
 * is one row per MRS line, the record, and an item planned for two MRS lines is still one item.
 */
function buildTableData(picklists: PicklistDto[]): PicklistParentRow[] {
  return picklists.map((picklist): PicklistParentRow => ({
    rowType: 'picklist',
    id: String(picklist.id),
    picklist,
    subRows: (picklist.itemGroups ?? []).map((group): PicklistItemRow => ({
      rowType: 'item',
      id: `${picklist.id}-${group.id}`,
      picklistId: picklist.id,
      item: group,
    })),
  }));
}

/**
 * Matches PL#, MRS# and item code — the same three fields the old dropdown matched. The codes come from
 * `items[]`, every MRS line's, which covers every item's code.
 */
function matchesQuery(picklist: PicklistDto, normalized: string): boolean {
  if (!normalized) return true;
  if (picklist.picklistNumber.toLowerCase().includes(normalized)) return true;
  if (picklist.mrsNumbers.some((mrs) => mrs.toLowerCase().includes(normalized))) return true;
  return picklist.items.some((item) => item.itemCode.toLowerCase().includes(normalized));
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

interface PicklistSubTableProps {
  picklists: PicklistDto[];
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void;
  /** Rendered inside the empty state on the Open tab only. */
  showCreateButton: boolean;
  tab: 'open' | 'closed';
  /** True when the empty result is the FILTER's doing rather than an empty list. */
  isFiltered: boolean;
  onClearFilter: () => void;
  sorting: SortingState;
  onSortingChange: (updater: SortingState | ((prev: SortingState) => SortingState)) => void;
  pagination: PaginationState;
  onPaginationChange: (updater: PaginationState | ((prev: PaginationState) => PaginationState)) => void;
  /** Server-side pagination: total pages. Its presence is what switches the table to manual mode. */
  pageCount?: number;
  /** Server-side pagination: the true row count, for the footer. Undefined ⇒ count the rows in hand. */
  totalCount?: number;
}

function PicklistSubTable({
  picklists,
  isLoading,
  error,
  onRefresh,
  showCreateButton,
  tab,
  isFiltered,
  onClearFilter,
  sorting,
  onSortingChange,
  pagination,
  onPaginationChange,
  pageCount: externalPageCount,
  totalCount,
}: PicklistSubTableProps) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const isServerSide = externalPageCount !== undefined;

  useEffect(() => {
    if (isServerSide) setExpanded({});
  }, [picklists, isServerSide]);

  const fromParam = tab === 'closed' ? '?from=closed' : '';

  const handleNavigateToDetail = useCallback(
    (picklistId: number) => navigate(`/mro/issuance/picklist/${picklistId}${fromParam}`),
    [navigate, fromParam],
  );

  const tableData = useMemo(() => buildTableData(picklists), [picklists]);

  const columns = useMemo(
    () => createPicklistColumns({ onNavigateToDetail: handleNavigateToDetail }),
    [handleNavigateToDetail],
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    // ALWAYS set these three; never spread them in conditionally. `useReactTable` keeps ONE table instance
    // and merges new options over the old ones, so a key that is merely ABSENT keeps whatever it was last
    // set to. Spreading `{}` on the client-paged tab therefore left the Closed tab's `manualPagination:
    // true` and `pageCount: 220` in place — the Open tab then skipped client pagination entirely (all 17
    // rows on a page of 10) and printed the Closed tab's page count. Reported 2026-09-01 and again
    // 2026-09-11; it never reproduced in between because the probe loads /picklists directly, and a fresh
    // instance has nothing to inherit. It only appears when you arrive from the Closed tab.
    manualPagination: isServerSide,
    manualSorting: isServerSide,
    pageCount: isServerSide ? externalPageCount : undefined,
    getSubRows: (row) => (row.rowType === 'picklist' ? row.subRows : undefined),
    onExpandedChange: setExpanded,
    onSortingChange,
    onPaginationChange,
    getRowId: (row) => row.id,
    paginateExpandedRows: isServerSide,
    autoResetPageIndex: false,
    autoResetExpanded: false,
    state: { expanded, sorting, pagination },
  });

  // First load only. On a refetch with rows already on screen the stale table stays visible rather than
  // blanking — the numbers are seconds old, not wrong.
  if (isLoading && picklists.length === 0) {
    return <TableSkeleton rows={8} cols={7} />;
  }

  // Scoped to the table region: the tabs, the filter and the toolbar stay mounted above this, so a failed
  // fetch does not take the whole surface (and the user's typed filter) with it.
  if (error) {
    return (
      <EmptyState
        icon={ClipboardList}
        title="Couldn't load picklists"
        description={error}
        action={
          <Button variant="outline" onClick={onRefresh}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        }
      />
    );
  }

  if (picklists.length === 0) {
    if (isFiltered) {
      return (
        <EmptyState
          icon={SearchX}
          title="No picklists match that filter"
          description="Nothing here matches the PL number, MRS number or item code you typed."
          action={
            <Button variant="outline" onClick={onClearFilter}>
              Clear filter
            </Button>
          }
        />
      );
    }
    return (
      <EmptyState
        icon={ClipboardList}
        title={tab === 'open' ? 'No open picklists' : 'No closed picklists'}
        description={
          tab === 'open'
            ? 'There are no active picklists. Create one to get started.'
            : 'Nothing has been closed or cancelled yet.'
        }
        action={
          showCreateButton ? (
            <PermissionGuard permission={PERMISSIONS.ISSUANCE_CREATE_PICKLIST}>
              <Button onClick={() => navigate('/mro/issuance/create')}>
                <Plus className="mr-2 h-4 w-4" />
                Create Picklist
              </Button>
            </PermissionGuard>
          ) : (
            <Button variant="outline" onClick={onRefresh}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          )
        }
      />
    );
  }

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;
  // Server-paged tabs know a total the client cannot count; client-paged ones do not.
  const rowCount = totalCount ?? picklists.length;
  const anyCommitted = picklists.some((p) => p.items.some((i) => i.postingQuantity > 0));

  return (
    <div className="space-y-3">
      {/* One card holds the header, the rows AND the footer — the footer is part of the table surface in
          the design, not a control strip floating beneath it. */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="h-10 bg-muted/40 hover:bg-muted/40">
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} className="text-xs font-medium text-muted-foreground">
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            // Routed through DataTableRow rather than mapping getVisibleCells() inline: the React
            // Compiler cannot track TanStack's mutable table state, and that shared component carries the
            // `use no memo` escape hatch once for every consumer (86d39bx22).
            <DataTableRow
              key={row.id}
              row={row}
              rowClassName={(r) =>
                r.original.rowType === 'item'
                  ? 'bg-muted/45 hover:bg-muted/60 !border-l-[3px] !border-l-primary/30'
                  : 'cursor-pointer hover:bg-muted/30'
              }
              onRowClick={(r) => {
                if (r.original.rowType === 'picklist') r.toggleExpanded();
              }}
            />
          ))}
        </TableBody>
      </Table>

      {/* Footer, inside the card. The design shows the row count on the left and the pager on the right.
          The page-size select is NOT in the design and is kept anyway: the Closed tab is server-paged, so
          it is the only way to see more than ten rows at a time. Recorded here rather than dropped
          silently — a fidelity divergence with a reason beats a lost control. */}
      <div className="flex items-center justify-between gap-4 border-t border-border px-4 py-2.5">
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {rowCount} picklist{rowCount === 1 ? '' : 's'}
          </span>
          <Select
            value={String(table.getState().pagination.pageSize)}
            onValueChange={(value) => {
              onPaginationChange((prev) => ({ ...prev, pageSize: Number(value), pageIndex: 0 }));
            }}
          >
            <SelectTrigger className="h-7 w-16 text-xs" aria-label="Rows per page">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size} / page
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">
            Page {currentPage + 1} of {pageCount || 1}
          </span>
          <div className="flex gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="Previous page"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Previous page</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="Next page"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Next page</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
      </div>

      <ProgressLegend showCommitted={anyCommitted} />
    </div>
  );
}

export function PicklistsTab() {
  const navigate = useNavigate();

  // Narrow selectors, one per value (86d3d1z40). A whole-store destructure here re-renders the table on
  // every settle-poll tick.
  const openPicklists = usePicklistStore((s) => s.openPicklists);
  const isLoadingOpen = usePicklistStore((s) => s.isLoadingOpen);
  const openError = usePicklistStore((s) => s.openError);
  const closedPicklists = usePicklistStore((s) => s.closedPicklists);
  const closedTotalPages = usePicklistStore((s) => s.closedTotalPages);
  const closedTotalCount = usePicklistStore((s) => s.closedTotalCount);
  const isLoadingClosed = usePicklistStore((s) => s.isLoadingClosed);
  const closedError = usePicklistStore((s) => s.closedError);
  const counts = usePicklistStore((s) => s.counts);
  const fetchOpenPicklists = usePicklistStore((s) => s.fetchOpenPicklists);
  const fetchClosedPicklists = usePicklistStore((s) => s.fetchClosedPicklists);
  const fetchCounts = usePicklistStore((s) => s.fetchCounts);

  const canViewAllPicklists = useAuthStore((s) => s.hasPermission(PERMISSIONS.ISSUANCE_VIEW_ALL_PICKLISTS));

  const {
    sorting: openSorting,
    pagination: openPagination,
    onSortingChange: onOpenSortingChange,
    onPaginationChange: onOpenPaginationChange,
    // resetPage is deliberately NOT taken here — see handleMineOnlyChange. Calling it alongside another
    // setSearchParams in the same tick is what silently ate the `mine=1` param.
  } = useTableStateParams({ prefix: 'open', defaultPageSize: 10 });

  const {
    sorting: closedSorting,
    pagination: closedPagination,
    onSortingChange: onClosedSortingChange,
    onPaginationChange: onClosedPaginationChange,
  } = useTableStateParams({ prefix: 'closed', defaultPageSize: 10 });

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'closed' ? 'closed' : 'open';
  // The store owns it, persisted to localStorage — ONE owner. It used to live in the URL as well, with an
  // effect reconciling the two; whichever settled last won, which is what made the toggle unreliable.
  // Dropping the URL copy loses a shared link carrying the scope, and that is fine: "only MY picklists"
  // resolves against whoever OPENS the link, so it never showed the recipient what the sender saw.
  const mineOnly = usePicklistStore((s) => s.mineOnly);
  const setMineOnly = usePicklistStore((s) => s.setMineOnly);

  const [query, setQuery] = useState('');

  const handleTabChange = useCallback(
    (value: 'open' | 'closed') => {
      setQuery('');
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value === 'open') next.delete('tab');
        else next.set('tab', value);
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  const handleMineOnlyChange = useCallback(
    (next: boolean) => {
      setMineOnly(next);
      // Both tabs return to page 1: scope changes how many pages exist, and a supervisor on page 7 of 40
      // would otherwise land out of range on an empty table. ONE setSearchParams doing both — React Router
      // resolves a functional updater against the params as of the last RENDER, so two calls in one tick
      // would each see the original and the last would win.
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.delete('open_page');
        params.delete('closed_page');
        return params;
      }, { replace: true });
    },
    [setMineOnly, setSearchParams],
  );

  const [closedTabActivated, setClosedTabActivated] = useState(activeTab === 'closed');

  useEffect(() => {
    fetchOpenPicklists();
  }, [fetchOpenPicklists, mineOnly]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts, mineOnly]);

  // 86d3kwt7h — while any open picklist has a MIS post in flight, refresh so the Posting / Failed pills
  // settle without a manual refresh. Stops the instant nothing is in flight.
  const anyPosting = useMemo(
    () => openPicklists.some((p) => (p.activePostingBatches ?? []).some((b) => b.status === PostingStatusEnum.Posting)),
    [openPicklists],
  );
  useEffect(() => {
    if (!anyPosting) return;
    const timer = setInterval(() => { fetchOpenPicklists(); }, 3000);
    return () => clearInterval(timer);
  }, [anyPosting, fetchOpenPicklists]);

  // The Closed list stays lazy. It runs the same fat object-graph projection as Open — items, bins, MIS
  // references, picking histories — so not fetching it until the tab is opened is load-bearing.
  const closedQuery = activeTab === 'closed' ? query : '';
  useEffect(() => {
    if (!closedTabActivated) return;
    fetchClosedPicklists({
      page: closedPagination.pageIndex + 1, // API is 1-based
      pageSize: closedPagination.pageSize,
      sortBy: closedSorting.length > 0 ? closedSorting[0].id : undefined,
      sortDesc: closedSorting.length > 0 ? closedSorting[0].desc : true,
      search: closedQuery || undefined,
    });
  }, [closedTabActivated, closedPagination.pageIndex, closedPagination.pageSize, closedSorting, closedQuery, fetchClosedPicklists, mineOnly]);

  useEffect(() => {
    if (activeTab === 'closed' && !closedTabActivated) setClosedTabActivated(true);
  }, [activeTab, closedTabActivated]);

  // A refresh re-issues the CURRENT page, sort and filter. Resetting to page 1 here would leave the table
  // and the address bar disagreeing with no error.
  const handleRefreshClosed = useCallback(() => {
    fetchClosedPicklists({
      page: closedPagination.pageIndex + 1,
      pageSize: closedPagination.pageSize,
      sortBy: closedSorting.length > 0 ? closedSorting[0].id : undefined,
      sortDesc: closedSorting.length > 0 ? closedSorting[0].desc : true,
      search: closedQuery || undefined,
    });
    fetchCounts();
  }, [closedSorting, closedPagination, closedQuery, fetchClosedPicklists, fetchCounts]);

  const handleRefreshOpen = useCallback(() => {
    fetchOpenPicklists();
    fetchCounts();
  }, [fetchOpenPicklists, fetchCounts]);

  // Open filters client-side over the unpaged list; Closed filters on the server, because the client
  // holds one page and cannot narrow what it never fetched.
  const filteredOpen = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return openPicklists;
    return openPicklists.filter((p) => matchesQuery(p, normalized));
  }, [openPicklists, query]);

  const rows = activeTab === 'open' ? filteredOpen : closedPicklists;
  const singleMatch = query.trim() !== '' && rows.length === 1 ? rows[0] : null;

  const handleSubmitSingleMatch = useCallback(() => {
    if (singleMatch) navigate(`/mro/issuance/picklist/${singleMatch.id}?from=${activeTab}`);
  }, [singleMatch, navigate, activeTab]);

  const mineOnClosed = mineOnly && activeTab === 'closed';
  const isRefreshing = activeTab === 'open' ? isLoadingOpen : isLoadingClosed;
  const handleRefresh = activeTab === 'open' ? handleRefreshOpen : handleRefreshClosed;

  return (
    <div className="space-y-4">
      {/* PT-7 B1 — the page's ONE MRS hover card for every parent-row chip. It fills its figures lazily, one
          request per picklist on the first hover of a row. */}
      <MrsHoverCardHost />
      {/* The header lives HERE rather than in PicklistsPage, and that is deliberate. Refresh must re-issue
          the CURRENT page, size, sort and filter — a shared handler that reaches for defaults drops a
          supervisor from page 7 to page 1 while `closed_page=7` is still in the address bar, with nothing
          to show it went wrong. Putting the button beside the state it has to read makes that mistake
          impossible rather than merely documented. */}
      <PageHeader
        title="Picklists"
        description="Pick, then post — a picklist isn't done until its picked stock is posted to NetSuite as MIS."
        actions={
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  aria-busy={isRefreshing}
                  aria-label="Refresh picklists"
                >
                  {/* Instant acknowledgement: the click shows before the network answers. */}
                  <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin motion-reduce:animate-none' : ''}`} />
                  Refresh
                </Button>
              </TooltipTrigger>
              <TooltipContent>Reload this tab, keeping your page and filter</TooltipContent>
            </Tooltip>
            <PermissionGuard permission={PERMISSIONS.ISSUANCE_CREATE_PICKLIST}>
              <Button size="sm" onClick={() => navigate('/mro/issuance/create')}>
                <Plus className="mr-2 h-4 w-4" />
                Create Picklist
              </Button>
            </PermissionGuard>
          </>
        }
      />

      <PicklistFilterBar
        tab={activeTab}
        onTabChange={handleTabChange}
        openCount={counts?.open ?? openPicklists.length}
        closedCount={counts?.closed ?? null}
        onQueryChange={setQuery}
        onSubmitSingleMatch={handleSubmitSingleMatch}
        singleMatchHint={
          singleMatch ? `Press Enter to open ${singleMatch.picklistNumber}.` : null
        }
        showMineToggle={canViewAllPicklists}
        mineOnly={mineOnly}
        onMineOnlyChange={handleMineOnlyChange}
      />

      {mineOnClosed && (
        // D31 — the Closed scope matches on CREATOR only. Without saying so, this toggle silently hides
        // closed picklists the user picked but did not create.
        <p className="text-xs text-muted-foreground">
          On the Closed tab, “my picklists” means the ones you <strong>created</strong> — picklists you
          picked for someone else are not shown.
        </p>
      )}

      {activeTab === 'open' ? (
        // `key` is load-bearing, not a lint appeasement. Both branches render the SAME component type at
        // the SAME position, so without it React reconciles them as one instance and the Open tab inherits
        // whatever the Closed tab left behind — its `expanded` map (the reset at the top of this component
        // fires only when isServerSide, i.e. only for Closed) and its pagination posture. Two logically
        // separate tables, one instance, is wrong on its own terms.
        <PicklistSubTable
          key="open"
          picklists={filteredOpen}
          isLoading={isLoadingOpen}
          error={openError}
          onRefresh={handleRefreshOpen}
          showCreateButton
          tab="open"
          isFiltered={query.trim() !== ''}
          onClearFilter={() => setQuery('')}
          sorting={openSorting}
          onSortingChange={onOpenSortingChange}
          pagination={openPagination}
          onPaginationChange={onOpenPaginationChange}
        />
      ) : (
        <PicklistSubTable
          key="closed"
          picklists={closedPicklists}
          isLoading={isLoadingClosed}
          error={closedError}
          onRefresh={handleRefreshClosed}
          showCreateButton={false}
          tab="closed"
          isFiltered={query.trim() !== ''}
          onClearFilter={() => setQuery('')}
          sorting={closedSorting}
          onSortingChange={onClosedSortingChange}
          pagination={closedPagination}
          onPaginationChange={onClosedPaginationChange}
          pageCount={closedTotalPages}
          totalCount={closedTotalCount}
        />
      )}
    </div>
  );
}
